"use client";

import { useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  type DragEndEvent,
  type DragStartEvent,
  useDroppable,
  useDraggable,
} from "@dnd-kit/core";
import { Bot, Plus } from "lucide-react";
import { useStore } from "@/lib/store";
import { STATUSES } from "@/lib/constants";
import type { Issue, StatusId } from "@/lib/types";
import { StatusIcon } from "@/components/ui/StatusIcon";
import { PriorityIcon } from "@/components/ui/PriorityIcon";
import { Avatar } from "@/components/ui/Avatar";
import { BotBadge } from "@/components/ui/BotBadge";
import { AwaitingMeBadge } from "@/components/issues/AwaitingMeBadge";
import { cn } from "@/lib/utils";

export function BoardView({ issues, canWrite }: { issues: Issue[]; canWrite: boolean }) {
  const updateIssue = useStore((s) => s.updateIssue);
  const setCreateOpen = useStore((s) => s.setCreateOpen);
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const byStatus = useMemo(() => {
    const g: Record<StatusId, Issue[]> = {
      backlog: [],
      todo: [],
      in_progress: [],
      in_review: [],
      done: [],
      canceled: [],
    };
    for (const i of issues) g[i.status].push(i);
    return g;
  }, [issues]);

  function handleDragStart(e: DragStartEvent) {
    if (!canWrite) return;
    setActiveId(String(e.active.id));
  }
  function handleDragEnd(e: DragEndEvent) {
    if (!canWrite) return;
    setActiveId(null);
    const overId = e.over?.id as string | undefined;
    const dragged = e.active.id as string;
    if (!overId) return;
    const targetStatus = overId.startsWith("col:") ? (overId.slice(4) as StatusId) : null;
    if (!targetStatus) return;
    const issue = issues.find((i) => i.id === dragged);
    if (!issue || issue.status === targetStatus) return;
    updateIssue(dragged, { status: targetStatus });
  }

  const activeIssue = activeId ? issues.find((i) => i.id === activeId) : null;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-x-auto overflow-y-hidden scrollbar-thin">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex h-full gap-3 px-4 py-3 min-w-max">
          {STATUSES.map((s) => (
            <Column
              key={s.id}
              status={s.id}
              title={s.name}
              issues={byStatus[s.id]}
              canWrite={canWrite}
              onAdd={canWrite ? () => setCreateOpen(true) : undefined}
            />
          ))}
        </div>
        <DragOverlay>
          {activeIssue ? (
            <div className="rotate-[1.5deg]">
              <IssueCard issue={activeIssue} overlay />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      </div>
    </div>
  );
}

function Column({
  status,
  title,
  issues,
  canWrite,
  onAdd,
}: {
  status: StatusId;
  title: string;
  issues: Issue[];
  canWrite: boolean;
  onAdd?: () => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `col:${status}`, disabled: !canWrite });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex flex-col w-[300px] shrink-0 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border)] transition-colors h-full max-h-full",
        isOver && "border-[var(--accent)] bg-[var(--accent-bg)]"
      )}
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--border)]">
        <StatusIcon status={status} size={13} />
        <span className="text-[12.5px] font-medium">{title}</span>
        <span className="text-[11.5px] text-[var(--fg-muted)]">{issues.length}</span>
        {onAdd && (
          <button
            onClick={onAdd}
            className="ml-auto w-5 h-5 rounded hover:bg-[var(--bg-hover)] flex items-center justify-center text-[var(--fg-tertiary)] transition-colors"
            title="New task"
          >
            <Plus size={12} />
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-thin px-2 py-2 flex flex-col gap-1.5">
        {issues.map((i) => (
          <DraggableCard key={i.id} issue={i} disabled={!canWrite} />
        ))}
        {issues.length === 0 && (
          <div className="flex-1 flex items-center justify-center text-[11.5px] text-[var(--fg-muted)] border border-dashed border-[var(--border)] rounded-md py-8">
            {canWrite ? "Drop here" : "No tasks"}
          </div>
        )}
      </div>
    </div>
  );
}

function DraggableCard({ issue, disabled }: { issue: Issue; disabled: boolean }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: issue.id,
    disabled,
  });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ opacity: isDragging ? 0.35 : 1 }}
    >
      <IssueCard issue={issue} />
    </div>
  );
}

function IssueCard({ issue, overlay }: { issue: Issue; overlay?: boolean }) {
  const users = useStore((s) => s.users);
  const labels = useStore((s) => s.labels);
  const selectIssue = useStore((s) => s.selectIssue);
  const compatibilityAssignee = users.find((u) => u.id === issue.assigneeId);
  const owner = users.find(
    (u) =>
      u.id ===
      (issue.ownerId ??
        (compatibilityAssignee && !compatibilityAssignee.isBot
          ? compatibilityAssignee.id
          : null))
  );
  const executor = users.find(
    (u) =>
      u.id ===
      (issue.executorAgentId ??
        (compatibilityAssignee?.isBot ? compatibilityAssignee.id : null))
  );
  const issueLabels = labels.filter((l) => issue.labelIds.includes(l.id));
  return (
    <div
      onClick={() => !overlay && selectIssue(issue.id)}
      className={cn(
        "group rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] p-2.5 cursor-pointer hover:border-[var(--border-strong)] hover:bg-[var(--bg-hover)] transition-colors",
        overlay && "shadow-lg border-[var(--border-strong)] bg-[var(--surface-elevated)]"
      )}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <PriorityIcon priority={issue.priority} size={13} />
        <span className="text-[11px] font-mono text-[var(--fg-muted)]">{issue.identifier}</span>
        <BotBadge authorId={issue.authorId} />
        <AwaitingMeBadge issue={issue} />
        <div className="ml-auto flex items-center gap-1.5">
          {executor && (
            <span
              className="inline-flex items-center gap-1 rounded border border-[var(--border)] bg-[var(--accent-bg)] px-1 py-0.5 text-[9.5px] text-[var(--brand-indigo)]"
              title={`Agent: ${executor.name}`}
            >
              <Bot size={9} />
              <span className="max-w-[54px] truncate">{executor.name}</span>
            </span>
          )}
          <Avatar user={owner} size={18} />
        </div>
      </div>
      <div className="text-[12.5px] font-medium leading-snug text-[var(--fg)]">
        {issue.title}
      </div>
      {issueLabels.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {issueLabels.slice(0, 2).map((l) => (
            <span
              key={l.id}
              className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] border border-[var(--border)] text-[var(--fg-secondary)]"
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: l.color }} />
              {l.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
